import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { ExportButton } from '@/components/data-table/ExportButton'
import { OptionSelect } from '@/components/filters/OptionSelect'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useQueryState } from '@/hooks/use-query-state'
import { formatDateTime } from '@/lib/format'
import { waitingFor } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import {
  hrApi,
  type HrRequest,
  type HrRequestDetail,
  type HrRequestStatus,
} from '@/services/http/hr'
import { HrPageHeader } from '@/features/hr/HrPageHeader'
import { ActionStatusBadge, DetailGrid, RecordHistory } from '@/features/hr/parts'
import { actionLabel, useRequestCounts } from '@/features/hr/hr-actions'
import { RequestFormSheet, type RequestFormTarget } from '@/features/hr/RequestForm'
import { BulkApproveButton } from '@/features/hr/bulk-approve'
import { requestsExport } from '@/features/hr/exports'
import { filterRequests, requestSummary, targetCopy } from '@/features/hr/request-copy'
import {
  DecisionPanel,
  ResolvePanel,
  ResubmitPanel,
  RetryPanel,
  RevisionNotice,
  WithdrawPanel,
} from '@/features/hr/request-panels'
import { formatLeaveRange } from '@/features/hr/request-value'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const r = t.approvals
const STATUSES: HrRequestStatus[] = ['onay_bekliyor', 'revizyonda', 'onaylandi', 'iptal']

const STATUS_TONES = {
  onay_bekliyor: 'info',
  revizyonda: 'warning',
  onaylandi: 'success',
  iptal: 'neutral',
} as const

export function RequestStatusBadge({ status }: { status: HrRequestStatus }) {
  return (
    <Badge tone={STATUS_TONES[status]} size="sm">
      {r.tabs[status]}
    </Badge>
  )
}

/** Gönderim: onaydan önce yok, sonra kuyruktaki aksiyonun durumu. */
/** Karar ya da düzeltme bekleyen talebin bekleme süresi (son güncellemeden beri). */
function WaitingCell({ request }: { request: HrRequest }) {
  if (request.status !== 'onay_bekliyor' && request.status !== 'revizyonda') {
    return <span className="text-text-muted text-xs">—</span>
  }
  return <>{waitingFor(request.updated_at)}</>
}

function DiaCell({ request }: { request: HrRequest }) {
  if (!request.action) return <span className="text-text-muted text-xs">—</span>
  return <ActionStatusBadge action={request.action} />
}

/** Detayda talebin konusu: giriş/çıkışta yön + an, izinde mevcut ve istenen hal. */
function requestFields(request: HrRequestDetail): [string, string][] {
  if (request.kind === 'izin_iptal') {
    return request.leave_old
      ? [
          [r.leave.current, `${request.leave_old.tur} · ${formatLeaveRange(request.leave_old)}`],
          [r.leave.requested, r.cancelLeave.requested],
        ]
      : [[r.leave.requested, r.cancelLeave.requested]]
  }
  if (request.leave_new) {
    const describe = (values: NonNullable<HrRequestDetail['leave_new']>) =>
      `${values.tur} · ${formatLeaveRange(values)}`
    return [
      ...(request.leave_old
        ? ([[r.leave.current, describe(request.leave_old)]] as [string, string][])
        : []),
      [r.leave.requested, describe(request.leave_new)],
    ]
  }
  const summary = requestSummary(request)
  return [
    [r.columns.tip, summary.tip],
    [r.columns.zaman, summary.zaman],
  ]
}

const linkClass = 'text-brand text-sm underline-offset-2 hover:underline'

/** Talepten kaydına: hareket, izin ve personel (karşı yönde kayıt geçmişi talebe bağlanır). */
function RecordLinks({ request }: { request: HrRequestDetail }) {
  return (
    <div className="flex flex-wrap gap-4">
      {request.hareket_id && (
        <Link
          to={`/ik/giris-cikis?id=${encodeURIComponent(request.hareket_id)}`}
          className={linkClass}
        >
          {r.links.movement}
        </Link>
      )}
      {request.leave_id && (
        <Link to={`/ik/izinler?id=${encodeURIComponent(request.leave_id)}`} className={linkClass}>
          {r.links.leave}
        </Link>
      )}
      <Link to={`/ik/personel?q=${encodeURIComponent(request.sicilno)}`} className={linkClass}>
        {r.links.person}
      </Link>
    </div>
  )
}

function RequestDetailSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const query = useQuery({
    queryKey: queryKeys.hr.request(id ?? ''),
    queryFn: () => hrApi.request(id ?? ''),
    enabled: id !== null,
    // DİA'ya gidiş sürerken durum kendiliğinden güncellensin.
    refetchInterval: (state) =>
      state.state.data?.action && ['bekliyor', 'calisiyor'].includes(state.state.data.action.status)
        ? 4000
        : false,
  })
  const request = query.data
  return (
    <Sheet open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            {request
              ? `${request.personel_adi ?? request.sicilno} · ${r.kind[request.kind]}`
              : r.title}
          </SheetTitle>
          <SheetDescription>{targetCopy(request).targetsNote}</SheetDescription>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4">
          {query.isLoading && <Loading rows={4} />}
          {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
          {request && (
            <>
              <RevisionNotice request={request} />
              <DetailGrid
                items={[
                  [t.filters.status, <RequestStatusBadge key="s" status={request.status} />],
                  [r.columns.firma, request.firma],
                  [t.detail.fields.sicil, request.sicilno],
                  ...requestFields(request),
                  [r.descriptionLabel, request.description],
                  ...(request.hareket_id
                    ? ([[r.original, `${request.hareket_id} · DİA ${request.dia_key ?? ''}`]] as [
                        string,
                        string,
                      ][])
                    : []),
                  [r.columns.acan, request.created_by.name ?? t.unknown],
                  [
                    targetCopy(request).diaStatus,
                    request.action ? (
                      <span key="dia" className="flex flex-wrap items-center gap-2">
                        {actionLabel(request.action.type)}
                        <ActionStatusBadge action={request.action} />
                        {request.action.message && (
                          <span className="text-text-muted text-xs">{request.action.message}</span>
                        )}
                      </span>
                    ) : request.status === 'iptal' ? (
                      targetCopy(request).diaCancelled
                    ) : request.status === 'revizyonda' ? (
                      targetCopy(request).diaRevision
                    ) : (
                      targetCopy(request).diaPending
                    ),
                  ],
                ]}
              />
              <RecordLinks request={request} />
              <DecisionPanel request={request} />
              <RetryPanel request={request} />
              <ResolvePanel request={request} />
              <ResubmitPanel key={request.updated_at} request={request} />
              <WithdrawPanel request={request} />
              <section className="border-border flex flex-col gap-2 border-t pt-4">
                <h3 className="text-sm font-medium">{r.thread}</h3>
                <div className="[--timeline-surface:var(--surface)]">
                  <RecordHistory kind="talep" id={request.id} />
                </div>
              </section>
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

export function ApprovalsPage() {
  const query = useQueryState()
  const status = (query.get('sekme') as HrRequestStatus | undefined) ?? 'onay_bekliyor'
  const selectedId = query.get('talep') ?? null
  const canCreate = useAuthStore((state) => state.hasPermission('hr.request:create'))
  const [newTarget, setNewTarget] = useState<RequestFormTarget | null>(null)
  const list = useQuery({
    queryKey: queryKeys.hr.requests(status),
    queryFn: () => hrApi.requests(status),
    refetchInterval: 30_000,
  })
  const counts = useRequestCounts()
  const canApprove = useAuthStore((state) => state.hasPermission('hr.request:approve'))
  const filter = {
    firma: query.get('firma') ?? '',
    kind: query.get('tur') ?? '',
    q: query.get('q') ?? '',
  }
  const all = useMemo(() => list.data ?? [], [list.data])
  const rows = filterRequests(all, filter)
  const firms = [...new Set(all.map((row) => row.firma))].sort((x, y) => x.localeCompare(y, 'tr'))
  // Çoklu seçim yalnız bekleyen sekmesinde ve onaylanabilir taleplerde (toplu onay).
  const selectable = status === 'onay_bekliyor' && canApprove
  const approvable = rows.filter((row) => row.can_approve)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const selected = approvable.filter((row) => picked.has(row.id)).map((row) => row.id)
  const toggle = (id: string, on: boolean) =>
    setPicked((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  return (
    <div className="flex flex-col gap-4">
      <HrPageHeader
        title={r.title}
        description={r.description}
        actions={
          <>
            <ExportButton filename={r.exportName} {...requestsExport(rows)} />
            {canCreate && (
              <Button size="sm" onClick={() => setNewTarget({ kind: 'giris_cikis_ekle' })}>
                <Plus /> {r.newRequest}
              </Button>
            )}
          </>
        }
      />
      <Tabs
        value={status}
        onValueChange={(value: HrRequestStatus) =>
          query.set({ sekme: value === 'onay_bekliyor' ? undefined : value })
        }
      >
        <TabsList>
          {STATUSES.map((item) => (
            <TabsTrigger key={item} value={item}>
              {r.tabs[item]}
              {counts && (
                <span className="text-text-muted ml-1.5 text-xs tabular-nums">
                  {counts.by_status[item] ?? 0}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="flex flex-wrap items-end gap-3">
        <OptionSelect
          className="w-56"
          aria-label={r.filters.firm}
          value={filter.firma}
          options={[
            { value: '', label: `${r.filters.firm}: ${r.filters.all}` },
            ...firms.map((value) => ({ value, label: value })),
          ]}
          onValueChange={(value) => query.set({ firma: value || undefined })}
        />
        <OptionSelect
          className="w-48"
          aria-label={r.filters.kind}
          value={filter.kind}
          options={[
            { value: '', label: `${r.filters.kind}: ${r.filters.all}` },
            ...Object.entries(r.kind).map(([value, label]) => ({ value, label })),
          ]}
          onValueChange={(value) => query.set({ tur: value || undefined })}
        />
        <Input
          key={filter.q}
          aria-label={r.filters.search}
          className="w-64"
          placeholder={r.filters.searchPlaceholder}
          defaultValue={filter.q}
          onBlur={(event) => query.set({ q: event.target.value || undefined })}
          onKeyDown={(event) => {
            if (event.key === 'Enter') query.set({ q: event.currentTarget.value || undefined })
          }}
        />
        {selectable && (
          <BulkApproveButton
            selected={selected}
            requests={rows}
            onDone={() => setPicked(new Set())}
          />
        )}
      </div>
      {list.isLoading ? (
        <Loading rows={4} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <Empty hint={r.empty} />
      ) : (
        <div className="border-border rounded-panel bg-surface overflow-x-auto border">
          <Table>
            <TableHeader>
              <TableRow>
                {selectable && (
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label={r.bulk.selectAll}
                      checked={approvable.length > 0 && selected.length === approvable.length}
                      onCheckedChange={(on) =>
                        setPicked(on ? new Set(approvable.map((row) => row.id)) : new Set())
                      }
                    />
                  </TableHead>
                )}
                <TableHead>{r.columns.personel}</TableHead>
                <TableHead>{r.columns.firma}</TableHead>
                <TableHead>{r.columns.tur}</TableHead>
                <TableHead>{r.columns.tip}</TableHead>
                <TableHead>{r.columns.zaman}</TableHead>
                <TableHead>{r.columns.acan}</TableHead>
                <TableHead>{r.columns.guncelleme}</TableHead>
                <TableHead>{r.columns.bekliyor}</TableHead>
                <TableHead>{r.columns.dia}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((request) => (
                <TableRow
                  key={request.id}
                  tabIndex={0}
                  className="cursor-pointer"
                  onClick={() => query.set({ talep: request.id })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') query.set({ talep: request.id })
                  }}
                >
                  {selectable && (
                    <TableCell
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        aria-label={`${r.bulk.select}: ${request.personel_adi ?? request.sicilno}`}
                        disabled={!request.can_approve}
                        checked={picked.has(request.id)}
                        onCheckedChange={(on) => toggle(request.id, on === true)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <Avatar name={request.personel_adi ?? request.sicilno} size="sm" />
                      <span className="flex flex-col leading-tight">
                        {request.personel_adi ?? t.unknown}
                        <span className="text-text-muted text-xs">{request.sicilno}</span>
                      </span>
                    </span>
                  </TableCell>
                  <TableCell>{request.firma}</TableCell>
                  <TableCell>{r.kind[request.kind]}</TableCell>
                  <TableCell>{requestSummary(request).tip}</TableCell>
                  <TableCell className="tabular-nums">{requestSummary(request).zaman}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <Avatar name={request.created_by.name ?? '?'} size="sm" />
                      {request.created_by.name ?? t.unknown}
                    </span>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {formatDateTime(request.updated_at)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    <WaitingCell request={request} />
                  </TableCell>
                  <TableCell>
                    <DiaCell request={request} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RequestDetailSheet id={selectedId} onClose={() => query.set({ talep: undefined })} />
      <RequestFormSheet target={newTarget} onClose={() => setNewTarget(null)} />
    </div>
  )
}
